<template>
  <CustomModal ref="modal" class="add-client-modal" title="Ajouter un client">
    <CustomForm ref="form" :inputs=inputs :submit=buttons.submit>
      <div class="client-form">

        <div class="client-sections">
          <ClientAccordionSection :step=1 title="Identité" :summary=identitySummary :is-open="openSection === 'identity'" :is-done=isIdentityDone :toggle-callback="() => toggleSection('identity')" :next-callback="() => openSection = 'address'" :is-next-disabled="!isIdentityDone">
            <div class="span-2 type-picker-row">
              <ClientTypePicker :content=inputs.type />
            </div>
            <p v-if="!inputs.type.value" class="span-2 client-type-prompt">Choisissez d’abord le type de client : la suite du formulaire s’adapte à votre choix.</p>
            <template v-else>
              <CustomTextField class="span-2" :content=inputs.name />
              <div v-if="isCompanyIdentifierDisplayed || isVatStatusDisplayed" class="span-2 identifier-row">
                <CustomTextField v-if="isCompanyIdentifierDisplayed" :content=inputs.siret />
                <CustomTextField v-if="isCompanyIdentifierDisplayed" :content=inputs.siren />
                <CustomSelectField v-if="isVatStatusDisplayed" :content=inputs.vatStatus />
              </div>
              <p v-if="isVatStatusDisplayed" class="span-2 field-hint">Un client en franchise en base de TVA reste assujetti.</p>
            </template>
          </ClientAccordionSection>

          <template v-if="inputs.type.value">
            <ClientAccordionSection :step=2 title="Adresse de facturation" :summary=addressSummary :is-open="openSection === 'address'" :is-done=isAddressDone :is-optional="true" :toggle-callback="() => toggleSection('address')" :next-callback="() => openSection = 'contact'">
              <CustomAddressField class="span-2" ref="addressField" :content=inputs.structuredAddress :client-type=inputs.type.value collapsible @update:value="value => inputs.address.value = value" />
            </ClientAccordionSection>

            <ClientAccordionSection :step=3 title="Coordonnées" :summary=contactSummary :is-open="openSection === 'contact'" :is-done=isContactDone :is-optional="true" :toggle-callback="() => toggleSection('contact')" :next-callback="() => openSection = 'followUp'">
              <CustomTextField :content=inputs.phone />
              <CustomTextField :content=inputs.mail />
              <CustomTextField :content=inputs.contact />
              <CustomTextField :content=inputs.other />
            </ClientAccordionSection>

            <ClientAccordionSection :step=4 title="Suivi commercial" :summary=followUpSummary :is-open="openSection === 'followUp'" :is-done=isFollowUpDone :is-optional="true" :toggle-callback="() => toggleSection('followUp')">
              <ClientStatusStepper class="span-2" :content=inputs.status />
              <ClientLastContact class="span-2" :content=inputs.lastContact />
              <CustomTextField class="span-2" :content=inputs.description />
              <CustomTextAreaField class="span-2" :content=inputs.note :auto-grow="true" :min-height="88"/>
            </ClientAccordionSection>
          </template>
        </div>

        <div class="client-form-footer">
          <span class="footer-status" :class="{'is-complete': missingRequiredCount === 0}">
            <span v-if="missingRequiredCount === 0" class="footer-status-check" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>
            <span class="footer-status-text">{{ missingRequiredText }}</span>
          </span>
          <CustomButton class="footer-cancel" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
          <CustomButton class="footer-submit" @click="submitForm" :params=buttons.submit />
        </div>
      </div>
    </CustomForm>
  </CustomModal>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark, faPlus, faUpLong, faCheck, faExclamation } from "@fortawesome/free-solid-svg-icons";
import {NotificationService} from "@vues/v-common";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import {ClientController} from "@/controllers/business/ClientController";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import CustomTextAreaField from "@/components/forms/customs/customTextAreaField/CustomTextAreaField.vue";
import {CLIENT_STATUS_ENUM, CLIENT_STATUS_ENUM_ARRAY} from "@/models/clientStatusEnum";
import {CLIENT_TYPE_ENUM, CLIENT_TYPE_ENUM_ARRAY, CLIENT_VAT_STATUS_ENUM, CLIENT_VAT_STATUS_ENUM_ARRAY, formatIdentifier, isClientVatStatusDisplayed, normalizeIdentifier, SIREN_MASK, SIRET_MASK, sirenFromSiret} from "@/utils";
import CustomAddressField from "@/components/forms/customs/customAddressField/CustomAddressField.vue";
import {formatAddressLine} from "@/components/forms/customs/customAddressField/addressSearch";
import ClientAccordionSection from "@/components/forms/ClientAccordionSection.vue";
import ClientTypePicker from "@/components/forms/ClientTypePicker.vue";
import ClientStatusStepper from "@/components/forms/ClientStatusStepper.vue";
import ClientLastContact from "@/components/forms/ClientLastContact.vue";
library.add(faXmark);
library.add(faPlus);
library.add(faCheck);
library.add(faUpLong);
library.add(faExclamation);

// Libellé d'une valeur d'énumération ({label, value}) ; vide si la valeur est inconnue
const labelOf = (values, value) => (values.find(option => option.value === value) || {}).label || '';

// Champ obligatoire vide ou valeur trop courte (même règle que CustomForm)
const isInputMissing = input => (input.required && (input.value === '' || input.value === undefined || input.value === null))
  || !!(input.minDigits && input.value && String(input.value).replace(/\D/g, '').length < input.minDigits);

  export default {
      name: 'AddClientModal',
    computed: {
      CLIENT_TYPE_ENUM() {
        return CLIENT_TYPE_ENUM
      },
      isVatStatusDisplayed() {
        return isClientVatStatusDisplayed(this.inputs.type.value)
      },
      // SIRET et SIREN : entreprises françaises et internationales
      isCompanyIdentifierDisplayed() {
        return this.inputs.type.value === CLIENT_TYPE_ENUM.FRENCH_COMPANY.value || this.inputs.type.value === CLIENT_TYPE_ENUM.INTERNATIONAL_COMPANY.value
      },
      // Même règle que CustomForm : champ obligatoire vide ou SIRET incomplet
      missingRequiredCount() {
        return Object.values(this.inputs).filter(input => input && isInputMissing(input)).length;
      },
      missingRequiredText() {
        const count = this.missingRequiredCount;
        if (count === 0) { return 'Tous les champs obligatoires sont remplis' }
        return count === 1 ? '1 champ obligatoire restant' : `${count} champs obligatoires restants`;
      },
      // Section « Identité » terminée : type, nom et SIRET complet s'il est saisi (il reste facultatif)
      isIdentityDone() {
        return ![this.inputs.type, this.inputs.name, this.inputs.siret].some(isInputMissing);
      },
      identitySummary() {
        return [this.inputs.name.value, labelOf(CLIENT_TYPE_ENUM_ARRAY, this.inputs.type.value), this.isCompanyIdentifierDisplayed ? formatIdentifier(this.inputs.siret.value, SIRET_MASK) : ''].filter(Boolean).join(' · ');
      },
      isAddressDone() { return !!this.inputs.address.value },
      addressSummary() { return formatAddressLine(this.inputs.address.value) || 'Rue, code postal, ville' },
      isContactDone() {
        return [this.inputs.phone, this.inputs.mail, this.inputs.contact, this.inputs.other].some(input => !!input.value);
      },
      contactSummary() {
        return [this.inputs.phone.value, this.inputs.mail.value, this.inputs.contact.value].filter(Boolean).join(' · ') || 'Téléphone, mail, contact';
      },
      isFollowUpDone() { return !!(this.inputs.lastContact.value || this.inputs.note.value || this.inputs.description.value) },
      followUpSummary() {
        const lastContact = this.inputs.lastContact.value ? new Date(this.inputs.lastContact.value) : null;
        const parts = [labelOf(CLIENT_STATUS_ENUM_ARRAY, this.inputs.status.value)];
        if (lastContact && !isNaN(lastContact)) { parts.push(`Dernier contact le ${lastContact.toLocaleDateString('fr-FR')}`) }
        if (this.inputs.note.value) { parts.push('note renseignée') }
        return parts.filter(Boolean).join(' · ');
      },
    },
      props: ['callback'],
      components: {ClientAccordionSection, ClientTypePicker, ClientStatusStepper, ClientLastContact, CustomAddressField, CustomTextAreaField, CustomSelectField, CustomModal, CustomForm, CustomTextField, CustomButton},
      data() {
          return {
            documentName: "test",
            inputs: {
              type: {label: 'Type de client', value: '', selectValues: CLIENT_TYPE_ENUM_ARRAY, required: true},
              vatStatus: {label: 'Statut TVA du client', value: CLIENT_VAT_STATUS_ENUM.UNKNOWN.value, type: 'select', selectValues: CLIENT_VAT_STATUS_ENUM_ARRAY},
              status: {label: 'Statut', value: '', type: 'select', selectValues: CLIENT_STATUS_ENUM_ARRAY, required: true},
              name: {label: 'Nom', value: '', type: 'text', required: true},
              description: {label: 'Description', value: '', type: 'text'},
              phone: {label: 'Téléphone', value: '', type: 'text'},
              mail: {label: 'Mail', value: '', type: 'text'},
              other: {label: 'Informations supplémentaires', value: '', type: 'text'},
              siret: {label: 'Siret', value: '', type: 'text', mask: SIRET_MASK},
              siren: {label: 'Siren', value: '', type: 'text', mask: SIREN_MASK},
              contact: {label: 'Contact', value: '', type: 'text'},
              structuredAddress: {street: '', streetNumber: '', addressLine2: '', postalCode: '', city: '', country: '', countryCode: ''},
              address: {value: ''},
              lastContact: {label: 'Dernier contact', value: '', type: 'date'},
              note: {label: 'Note', value: ''},
            },
            buttons:{
              submit: {text: "Ajouter", disabled: true},
              cancel: {text: "Annuler"},
            },
            isTerminateButtonDisplayed: false,
            openSection: 'identity',
          };
      },
      watch: {
        'inputs.type.value'(type) {
          const isFrenchCompany = type === CLIENT_TYPE_ENUM.FRENCH_COMPANY.value;
          this.inputs.siret.minDigits = isFrenchCompany ? SIRET_MASK.maxDigits : 0;
        },
        'inputs.siret.value'(siret) {
          const siren = sirenFromSiret(siret);
          if (siren) { this.inputs.siren.value = siren }
        }
      },
      methods: {
        openModal() {
          this.$refs.modal.openModal();
          this.$nextTick(() => this.$refs.modal.$el.querySelector('.content')?.scrollTo(0, 0));
          this.buttons.submit.disabled = true;
          this.openSection = 'identity';
          this.inputs.status.value = CLIENT_STATUS_ENUM.CONTACT.value;
          this.inputs.type.value = '';
          this.inputs.vatStatus.value = CLIENT_VAT_STATUS_ENUM.UNKNOWN.value;
          this.inputs.name.value = '';
          this.inputs.description.value = '';
          this.inputs.phone.value = '';
          this.inputs.mail.value = '';
          this.inputs.other.value = '';
          this.inputs.siret.value = '';
          this.inputs.siren.value = '';
          this.inputs.contact.value = '';
          this.inputs.structuredAddress = {street: '', streetNumber: '', addressLine2: '', postalCode: '', city: '', country: '', countryCode: ''};
          this.inputs.address.value = '';
          this.inputs.lastContact.value = '';
          this.inputs.note.value = '';
        },
        toggleSection(section) {
          this.openSection = this.openSection === section ? '' : section;
        },
        closeModal(event) {
          this.$refs.modal?.closeModal(event);
        },
        terminate(){
          this.closeModal(true);
          this.callback();
        },
        async submitForm() {
          const res = await this.$refs.form.submitForm(() => ClientController.post({
            type : this.inputs.type.value,
            ...(this.isVatStatusDisplayed ? { vatStatus : this.inputs.vatStatus.value } : {}),
            status: this.inputs.status.value,
            description: this.inputs.description.value,
            contact: this.inputs.contact.value,
            lastContact: this.inputs.lastContact.value,
            note: this.inputs.note.value,
            structuredAddress: this.$refs.addressField.getValue(),
            structuredInformations : {
              name: this.inputs.name.value,
              phoneNumber: this.inputs.phone.value,
              mail: this.inputs.mail.value,
              siret : normalizeIdentifier(this.inputs.siret.value, SIRET_MASK.maxDigits),
              siren : normalizeIdentifier(this.inputs.siren.value, SIREN_MASK.maxDigits),
              other : this.inputs.other.value,
            }
          }));
          if (!res.errors) {
            // TODO Ouvrir la modale d'édition
            (new NotificationService).addNotification("success", "Client ajouté");
            this.terminate();
          }
        }
      }
  }

</script>

<style lang="stylus" scoped>
@require '~/src/components/modals/clientForm.styl';
</style>

<style lang="stylus">
@require '~/src/components/modals/clientModalShell.styl';
</style>
